import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { EditOutlined } from '@ant-design/icons';
import { dbCols, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { Exposure } from '../../schema/be-graphql-generated';
import { useContext, useEffect } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { canEditExposure } from './esHelpers';
import useStyles from './styles.style';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const ExposuresList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const { styles } = useStyles();
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, setFilters } = useTable<Exposure>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'exposures',
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      operation: 'exposures',
      fields: [
        {
          data: ['_id', 'userId', 'updatedAt', 'type', 'fear', 'obsession'],
        },
        'count',
      ],
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  useActivityTracking(dbCols.exposures, fixedClientId, !fixedClientId);

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('shared.exposure.titlePlural')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-exposure-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-exposure-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/exposures/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        data-testid="exposures-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: DownwardArrow) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}

        <Table.Column<Exposure>
          key="type"
          dataIndex="type"
          title={translate('shared.exposure.type')}
          render={(text, record, indx) => {
            return (
              translate('shared.exposure.types', {
                returnObjects: true,
                // @ts-ignore
              })?.[record?.type || 0]?.name || ''
            );
          }}
        />

        <Table.Column<Exposure>
          key="fear"
          dataIndex="fear"
          title={translate('shared.exposure.fear')}
          render={(text, record, indx) => {
            return record?.type === 0
              ? record?.fear || ''
              : record?.type === 1
              ? record?.obsession || ''
              : '';
          }}
        />

        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />

        <Table.Column<Exposure>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            // ONE edit gate for all three exposure screens (esHelpers) — the list, the hierarchy
            // table and the situation editor asked it three times, inline, with the same four-line
            // expression; that is the shape that let E02's surfaces disagree about who may be
            // edited (founder QA 2026-09-02, defect A)
            const gate = canEditExposure(
              clients.find((c) => c._id === record.userId),
              {
                isTherapistAdmin:
                  identity?.db?.role === UserRole.THERAPIST_ADMIN,
              },
            );

            return (
              <Space>
                {gate.offered && (
                  <Tooltip title={translate(gate.tooltipKey)}>
                    <Link
                      to={`/tools/exposures/create?exposureId=${record._id}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!gate.enabled}
                        data-testid="edit-exposure-button"
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
